import {
  Avatar, Button, Checkbox, CommentThread, DatePicker, DetailPanel, FileUpload, InlineMessage, Input, Meter, Modal, ModalContent, OptionSelect, RecordPage,
  Spinner, Stack, Text, Textarea, TextLink, type UploadItem,
} from '@olytic/foundation';
import { type ReactNode, useCallback, useEffect, useState } from 'react';

import { authenticatedFetch } from '../auth/authClient';
import { useLogTime } from './logTime';
import { CopyGlyph, ExternalGlyph, PlusGlyph, PriorityMark, TypeMark } from './marks';
import {
  assigneeOptions, assigneePatch, assigneeValue, commentThreadEntries, dueState, hoursText, isBlocked, LINK_LABEL, PRIORITIES, shortDate, STATUS_LABEL, STATUSES, todayIso, TYPES,
} from './model';
import { ATTACH_ACCEPT, attachmentOf } from './WorkFiles';
import type { AvatarOf } from './WorkPage';
import {
  createItem, createLink, createWorkComment, deleteItem, deleteLink, deleteTime, deleteWorkComment, fetchItemDetail, fetchItems, fetchWorkItemComments,
  type LinkType, openFile, removeFile, updateItem, updateWorkComment, uploadFile, type WorkBootstrap, type WorkComment, type WorkItem,
  type WorkItemDetail, type WorkItemPatch, type WorkPriority, type WorkStatus, type WorkType,
} from './workApi';

const LINK_CHOICES: { value: string; label: string }[] = [
  { value: 'blocks:out', label: 'blocks' }, { value: 'blocks:in', label: 'is blocked by' },
  { value: 'relates:out', label: 'relates to' },
  { value: 'duplicates:out', label: 'duplicates' }, { value: 'duplicates:in', label: 'is duplicated by' },
];

/** A work item, large and centered: its content on the left, its fields on the right. */
export function WorkItemModal({
  itemId, boot, avatarOf, onOpen, onClose, onChanged, say,
}: {
  itemId: string;
  boot: WorkBootstrap;
  avatarOf: AvatarOf;
  onOpen: (id: string) => void;
  onClose: () => void;
  /** The saved item (null after a delete); `structural` when other items may have changed too. */
  onChanged: (item: WorkItem | null, structural: boolean) => void;
  say: (text: string, tone?: 'info' | 'error') => void;
}): JSX.Element {
  const [detail, setDetail] = useState<WorkItemDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  const [siblings, setSiblings] = useState<WorkItem[]>([]);
  const [linkable, setLinkable] = useState<WorkItem[]>([]);
  const [comments, setComments] = useState<WorkComment[]>([]);
  const [commentsBusy, setCommentsBusy] = useState(false);
  const [commentsError, setCommentsError] = useState<string | null>(null);
  const [description, setDescription] = useState('');
  const [child, setChild] = useState('');
  const [label, setLabel] = useState('');
  const [link, setLink] = useState({ type: 'blocks:out', to: '' });
  const [uploading, setUploading] = useState<UploadItem[]>([]);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const logTime = useLogTime();
  const today = todayIso();

  useEffect(() => {
    let live = true;
    fetchItemDetail(authenticatedFetch, itemId)
      .then((d) => {
        if (!live) return;
        setDetail(d);
        setDescription(d.item.description ?? '');
      })
      .catch((e: unknown) => live && setError(e instanceof Error ? e.message : 'Could not load the work item.'));
    return () => {
      live = false;
    };
  }, [itemId, reload, logTime.savedAt]);

  useEffect(() => {
    let live = true;
    setComments([]);
    setCommentsBusy(true);
    setCommentsError(null);
    fetchWorkItemComments(authenticatedFetch, itemId)
      .then(({ comments: loaded }) => live && setComments(loaded))
      .catch((e: unknown) => live && setCommentsError(e instanceof Error ? e.message : 'Could not load the comments.'))
      .finally(() => live && setCommentsBusy(false));
    return () => {
      live = false;
    };
  }, [itemId]);

  const projectId = detail?.item.project_id;
  useEffect(() => {
    if (!projectId) return undefined;
    let live = true;
    fetchItems(authenticatedFetch, { project: projectId }).then((l) => live && setSiblings(l)).catch(() => undefined);
    fetchItems(authenticatedFetch, { all: true }).then((l) => live && setLinkable(l)).catch(() => undefined);
    return () => {
      live = false;
    };
  }, [projectId, reload]);

  const refresh = useCallback(() => setReload((n) => n + 1), []);
  const saveComment = async (request: () => Promise<unknown>) => {
    setCommentsBusy(true);
    setCommentsError(null);
    try {
      await request();
      const { comments: loaded } = await fetchWorkItemComments(authenticatedFetch, itemId);
      setComments(loaded);
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Could not save the comment.';
      setCommentsError(message);
      say(message, 'error');
      throw e;
    } finally {
      setCommentsBusy(false);
    }
  };
  const patch = async (body: WorkItemPatch, structural = false) => {
    if (!detail) return;
    try {
      const saved = await updateItem(authenticatedFetch, detail.item.id, body);
      setDetail((d) => (d ? { ...d, item: saved } : d));
      onChanged(saved, structural);
      if (structural) refresh();
    } catch (e) {
      say(e instanceof Error ? e.message : 'Could not save the change.', 'error');
      refresh();
    }
  };

  const item = detail?.item;
  const project = item ? boot.projects.find((p) => p.id === item.project_id) : undefined;

  const crumbs: ReactNode = item ? (
    <span className="flex flex-wrap items-center gap-1.5 text-xs font-normal text-[var(--fg-muted)]">
      {item.parent_id && item.parent_key ? (
        <>
          {item.parent_type ? <TypeMark type={item.parent_type} /> : null}
          <Button variant="link" className="text-xs" onClick={() => onOpen(item.parent_id!)}>{item.parent_key}</Button>
          <span className="text-[var(--fg-faint)]">/</span>
        </>
      ) : null}
      <TypeMark type={item.type} />
      <span className="text-xs text-[var(--fg-strong)]">{item.key}</span>
    </span>
  ) : 'Work item';

  const copy = () => {
    if (!item) return;
    void navigator.clipboard?.writeText(`${item.key} ${item.title}`).then(() => say(`Copied ${item.key}`), () => say(item.key));
  };

  let body: ReactNode;
  if (error) body = <div className="p-6"><InlineMessage tone="error" role="alert">{error}</InlineMessage></div>;
  else if (!detail || !item || !project) body = <div className="flex justify-center py-16"><Spinner label="Loading the work item" /></div>;
  else {
    const kids = detail.children;
    const doneKids = kids.filter((k) => k.status === 'done').length;
    const childLabel = item.type === 'epic' ? 'Child work items' : 'Subtasks';
    const assigneeKey = assigneeValue(item);
    const assignee = avatarOf(assigneeKey);
    const assigneeHref = item.assignee_id ? `/people/${item.assignee_id}` : item.assignee_contact_id ? `/contacts/${item.assignee_contact_id}` : null;
    const parents = siblings.filter((x) => x.id !== item.id && (item.type === 'subtask' ? x.type !== 'epic' && x.type !== 'subtask' : x.type === 'epic'));
    const loggedOwn = detail.time.reduce((n, e) => n + e.hours, 0);
    const person = (id: string | null) => boot.people.find((p) => p.id === id)?.name ?? 'Someone';
    const due = dueState(item, today);

    const addFiles = async (list: File[]) => {
      setUploading(list.map((f, i) => ({ id: `up-${i}`, name: f.name, size: f.size, state: 'uploading' })));
      let done = 0;
      for (const f of list) {
        try {
          await uploadFile(authenticatedFetch, 'work_item', item.id, f);
          done += 1;
        } catch (e) {
          say(e instanceof Error ? e.message : `Could not attach ${f.name}.`, 'error');
        }
      }
      setUploading([]);
      if (done) say(`Attached ${done} file${done === 1 ? '' : 's'} to ${item.key}`);
      refresh();
    };

    const main = (
      <Stack gap="lg">
        <Section title="Description">
          <Textarea
            aria-label="Description"
            dictation
            rows={4}
            placeholder="Add the context, the acceptance criteria and what done looks like"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            onBlur={() => {
              if (description !== (item.description ?? '')) void patch({ description: description || null });
            }}
          />
        </Section>

        {item.type !== 'subtask' ? (
          <Section title={childLabel} aside={kids.length ? `${doneKids} of ${kids.length} done` : undefined}>
            {kids.length ? <Meter label={`${childLabel} done`} value={doneKids / kids.length} tone="accent" /> : null}
            <Rows>
              {kids.map((k) => {
                const a = avatarOf(assigneeValue(k));
                return (
                  <Row key={k.id}>
                    <TypeMark type={k.type} />
                    <Button variant="link" className={k.status === 'done' ? 'text-xs line-through' : 'text-xs'} onClick={() => onOpen(k.id)}>{k.key}</Button>
                    <span className="min-w-0 flex-1 truncate text-[var(--fg-strong)]">{k.title}</span>
                    {isBlocked(k) ? <span className="text-xs font-emphasis text-[var(--warning-fg)]">Blocked</span> : null}
                    <span className={k.status === 'done' ? 'text-xs text-[var(--success-fg)]' : 'text-xs text-[var(--fg-muted)]'}>{STATUS_LABEL[k.status]}</span>
                    {a ? <Avatar size="xs" name={a.name} {...(a.src ? { src: a.src } : {})} /> : null}
                  </Row>
                );
              })}
              <Row>
                <PlusGlyph />
                <Input
                  aria-label={`New ${childLabel.toLowerCase()}`}
                  placeholder={item.type === 'epic' ? 'Add a task to this epic and press Enter' : 'Add a subtask and press Enter'}
                  value={child}
                  onChange={(e) => setChild(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter' || !child.trim()) return;
                    const t = child.trim();
                    setChild('');
                    createItem(authenticatedFetch, { project_id: item.project_id, title: t, parent_id: item.id, type: item.type === 'epic' ? 'task' : 'subtask' })
                      .then((c) => {
                        say(`Created ${c.key}`);
                        onChanged(null, true);
                        refresh();
                      })
                      .catch((err: unknown) => say(err instanceof Error ? err.message : 'Could not create it.', 'error'));
                  }}
                />
              </Row>
            </Rows>
          </Section>
        ) : null}

        <Section title="Files" aside="Also listed on the project’s Files tab">
          <FileUpload
            variant="attachments"
            accept={ATTACH_ACCEPT}
            maxSizeMb={15}
            attachLabel="Attach files"
            attachHint="or drop them here"
            items={[...detail.files.map((f) => attachmentOf(f, boot)), ...uploading]}
            onAdd={(list) => void addFiles(list)}
            onOpen={(id) => {
              const f = detail.files.find((x) => x.id === id);
              if (f) openFile(authenticatedFetch, f).catch((e: unknown) => say(e instanceof Error ? e.message : 'Could not open the file.', 'error'));
            }}
            onRemove={(id) => {
              removeFile(authenticatedFetch, id, 'work_item', item.id)
                .then(refresh)
                .catch((e: unknown) => say(e instanceof Error ? e.message : 'Could not remove the file.', 'error'));
            }}
          />
        </Section>

        <Section title="Comments">
          {commentsError ? <InlineMessage tone="error" role="alert">{commentsError}</InlineMessage> : null}
          <CommentThread
            entries={commentThreadEntries(comments, (personId) => avatarOf(personId)?.src)}
            busy={commentsBusy}
            placeholder="Write a comment…"
            onPost={(body) => saveComment(() => createWorkComment(authenticatedFetch, item.id, body))}
            onEdit={(commentId, body) => saveComment(() => updateWorkComment(authenticatedFetch, commentId, body))}
            // Nothing waits on a delete, and saveComment has already shown its error.
            onDelete={(commentId) => saveComment(() => deleteWorkComment(authenticatedFetch, commentId)).catch(() => undefined)}
          />
        </Section>

        <Section title="Linked work items">
          <Rows>
            {detail.links.map((l) => (
              <Row key={l.id}>
                <span className="w-24 shrink-0 text-xs text-[var(--fg-muted)]">{LINK_LABEL[l.link_type][l.outgoing ? 0 : 1]}</span>
                <TypeMark type={l.other_type} />
                <Button variant="link" className={l.other_status === 'done' ? 'text-xs line-through' : 'text-xs'} onClick={() => onOpen(l.other_id)}>{l.other_key}</Button>
                <span className="min-w-0 flex-1 truncate text-[var(--fg-strong)]">{l.other_title}</span>
                <span className={l.other_status === 'done' ? 'text-xs text-[var(--success-fg)]' : 'text-xs text-[var(--fg-muted)]'}>{STATUS_LABEL[l.other_status]}</span>
                <Button
                  variant="subtle"
                  size="icon-sm"
                  aria-label={`Remove the link to ${l.other_key}`}
                  onClick={() => deleteLink(authenticatedFetch, l.id).then(() => {
                    onChanged(null, true);
                    refresh();
                  }).catch((e: unknown) => say(e instanceof Error ? e.message : 'Could not remove the link.', 'error'))}
                >
                  ×
                </Button>
              </Row>
            ))}
            <Row wrap>
              <div className="w-36 shrink-0">
                <OptionSelect aria-label="Link type" options={LINK_CHOICES} value={link.type} onValueChange={(type) => setLink((x) => ({ ...x, type }))} />
              </div>
              <div className="min-w-48 flex-1">
                <OptionSelect
                  aria-label="Work item to link"
                  options={linkable.filter((x) => x.id !== item.id).map((x) => ({ value: x.id, label: `${x.key} · ${x.title}` }))}
                  value={link.to}
                  placeholder="Choose a work item…"
                  onValueChange={(to) => setLink((x) => ({ ...x, to }))}
                />
              </div>
              <Button
                size="sm"
                disabled={!link.to}
                onClick={() => {
                  const [type, dir] = link.type.split(':') as [LinkType, 'out' | 'in'];
                  const body = dir === 'out'
                    ? { from_item_id: item.id, to_item_id: link.to, link_type: type }
                    : { from_item_id: link.to, to_item_id: item.id, link_type: type };
                  createLink(authenticatedFetch, body).then(() => {
                    setLink({ type: link.type, to: '' });
                    onChanged(null, true);
                    refresh();
                  }).catch((e: unknown) => say(e instanceof Error ? e.message : 'Could not link them.', 'error'));
                }}
              >
                Link
              </Button>
            </Row>
          </Rows>
        </Section>

        <Section title="Time" aside={`${hoursText(loggedOwn)} h logged`}>
          <div>
            <Button onClick={() => logTime.open()}><PlusGlyph />Log time on {item.key}</Button>
          </div>
          {detail.time.length ? (
            <Rows>
              {detail.time.map((e) => {
                const a = avatarOf(e.owner_id);
                return (
                  <Row key={e.id}>
                    <span className="w-14 shrink-0 text-xs tabular-nums text-[var(--fg-muted)]">{shortDate(e.entry_date)}</span>
                    {a ? <Avatar size="xs" name={a.name} {...(a.src ? { src: a.src } : {})} /> : null}
                    <span className="min-w-0 flex-1 truncate text-[var(--fg)]">
                      {e.notes || 'No notes'}
                      {e.created_by_id && e.created_by_id !== e.owner_id ? <span className="text-[var(--fg-muted)]"> · logged by {person(e.created_by_id)}</span> : null}
                    </span>
                    {e.billable ? null : <span className="text-xs text-[var(--fg-muted)]">Non-billable</span>}
                    <span className="font-emphasis tabular-nums text-[var(--fg-strong)]">{hoursText(e.hours)} h</span>
                    <Button variant="link" size="sm" onClick={() => logTime.open(e)}>Edit</Button>
                    <Button
                      variant="subtle"
                      size="icon-sm"
                      aria-label="Delete entry"
                      onClick={() => deleteTime(authenticatedFetch, e.id).then(refresh).catch((err: unknown) => say(err instanceof Error ? err.message : 'Could not delete it.', 'error'))}
                    >
                      ×
                    </Button>
                  </Row>
                );
              })}
            </Rows>
          ) : null}
        </Section>
      </Stack>
    );

    const rail = (
      <Stack gap="md">
        <div className="flex flex-wrap items-center gap-3">
          <OptionSelect
            aria-label="Status"
            options={STATUSES.map((s) => ({ value: s.key, label: s.label }))}
            value={item.status}
            onValueChange={(status) => void patch({ status: status as WorkStatus }, true)}
          />
          <label className="flex items-center gap-2 text-sm text-[var(--fg)]">
            <Checkbox checked={item.is_blocked} onCheckedChange={(v) => void patch({ is_blocked: v === true })} />
            Flag as blocked
          </label>
          {isBlocked(item) ? (
            <span className="text-xs font-emphasis text-[var(--warning-fg)]">
              {item.is_blocked ? 'Blocked' : `Blocked by ${item.blocked_by.join(', ')}`}
            </span>
          ) : null}
        </div>
        <DetailPanel
          columns={1}
          layout="rows"
          fields={[
            {
              key: 'assignee',
              label: 'Assignee',
              value: (
                <OptionSelect
                  aria-label="Assignee"
                  options={assigneeOptions(boot, item.project_id, item)}
                  value={assigneeKey ?? ''}
                  emptyLabel="Unassigned"
                  onValueChange={(v) => void patch(assigneePatch(v || null))}
                />
              ),
              action: assigneeHref ? (
                <TextLink href={assigneeHref} target="_blank" rel="noopener" aria-label={`Open ${assignee?.name ?? 'the assignee'} in a new tab`} title="Open in a new tab">
                  <ExternalGlyph />
                </TextLink>
              ) : undefined,
            },
            {
              key: 'priority',
              label: 'Priority',
              value: (
                <OptionSelect aria-label="Priority" options={PRIORITIES.map(([value, l]) => ({ value, label: l }))} value={item.priority} onValueChange={(v) => void patch({ priority: v as WorkPriority })} />
              ),
              action: <PriorityMark priority={item.priority} />,
            },
            {
              key: 'type',
              label: 'Type',
              value: (
                <OptionSelect aria-label="Type" options={TYPES.map(([value, l]) => ({ value, label: l }))} value={item.type} onValueChange={(v) => void patch({ type: v as WorkType }, true)} />
              ),
              action: <TypeMark type={item.type} />,
            },
            ...(item.type !== 'epic' ? [{
              key: 'parent',
              label: 'Parent',
              value: (
                <OptionSelect
                  aria-label="Parent"
                  options={parents.map((x) => ({ value: x.id, label: `${x.key} · ${x.title}` }))}
                  value={item.parent_id ?? ''}
                  emptyLabel="None"
                  onValueChange={(v) => void patch({ parent_id: v || null }, true)}
                />
              ),
              action: item.parent_id ? (
                <Button variant="subtle" size="icon-sm" aria-label={`Open ${item.parent_key ?? 'the parent'}`} title={`Open ${item.parent_key ?? 'the parent'}`} onClick={() => onOpen(item.parent_id!)}>
                  <ExternalGlyph />
                </Button>
              ) : undefined,
            }] : []),
            {
              key: 'due',
              label: 'Due date',
              value: (
                <DatePicker
                  aria-label="Due date"
                  className={due === 'overdue' ? 'text-[var(--danger-fg)]' : due === 'today' ? 'text-[var(--warning-fg)]' : undefined}
                  value={item.due_date ?? ''}
                  onChange={(next) => void patch({ due_date: next || null })}
                />
              ),
            },
            {
              key: 'labels',
              label: 'Labels',
              value: (
                <span className="flex flex-wrap items-center gap-1.5">
                  {item.labels.map((l) => (
                    <span key={l} className="oly-collabel inline-flex items-center gap-1 rounded-[var(--radius-sm)] border border-[var(--border-strong)] pl-1.5">
                      {l}
                      <Button variant="subtle" size="icon-sm" aria-label={`Remove ${l}`} onClick={() => void patch({ labels: item.labels.filter((x) => x !== l) })}>×</Button>
                    </span>
                  ))}
                  <Input
                    aria-label="Add label"
                    placeholder="Add label"
                    className="w-28"
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key !== 'Enter') return;
                      const v = label.trim().toLowerCase().replace(/\s+/g, '-');
                      if (!v) return;
                      setLabel('');
                      if (!item.labels.includes(v)) void patch({ labels: [...item.labels, v] });
                    }}
                  />
                </span>
              ),
            },
            {
              key: 'project',
              label: 'Project',
              value: (
                <TextLink href={`/projects/${project.id}`} target="_blank" rel="noopener" title="Open the project record in a new tab">
                  <span className="text-xs">{project.code}</span> {project.name} <ExternalGlyph />
                </TextLink>
              ),
            },
            {
              key: 'account',
              label: 'Account',
              value: (
                <TextLink href={`/accounts/${project.account_id}`} target="_blank" rel="noopener" title="Open the account record in a new tab">
                  {project.account_name} <ExternalGlyph />
                </TextLink>
              ),
            },
          ]}
        />
      </Stack>
    );

    const railFooter = (
      <Stack gap="sm">
        <Text size="sm" tone="muted" className="flex flex-col gap-0.5">
          <span>Created {shortDate(item.created_at)}{item.created_by_id ? ` by ${person(item.created_by_id)}` : ''}</span>
          <span>
            Updated {shortDate(item.updated_at)}
            {item.closed_at ? ` · Closed ${shortDate(item.closed_at)}` : ''}{item.archived_at ? ` · Archived ${shortDate(item.archived_at)}` : ''}
          </span>
        </Text>
        <div className="flex flex-wrap gap-4">
          <Button variant="link" onClick={() => void patch({ archived: !item.archived_at }, true).then(() => say(item.archived_at ? `Restored ${item.key}` : `Archived ${item.key}`))}>
            {item.archived_at ? 'Restore from archive' : 'Archive'}
          </Button>
          <Button
            variant="destructive-subtle"
            size="sm"
            onClick={() => {
              if (!confirmDelete) {
                setConfirmDelete(true);
                return;
              }
              deleteItem(authenticatedFetch, item.id).then(({ deleted }) => {
                say(`Deleted ${item.key}${deleted > 1 ? ` and ${deleted - 1} child items` : ''}`);
                onChanged(null, true);
                onClose();
              }).catch((e: unknown) => say(e instanceof Error ? e.message : 'Could not delete it.', 'error'));
            }}
          >
            {confirmDelete ? `Click again to delete ${item.key}${kids.length ? ' and its children' : ''}` : 'Delete work item'}
          </Button>
        </div>
      </Stack>
    );

    body = (
      <RecordPage
        variant="dialog"
        objectLabel="Work item"
        title={item.title}
        titlePlaceholder="Name this work item"
        onTitleChange={(t) => void patch({ title: t })}
        main={main}
        rail={rail}
        railFooter={railFooter}
      />
    );
  }

  return (
    <Modal open onOpenChange={(open) => (open ? undefined : onClose())}>
      <ModalContent
        size="record"
        title={crumbs}
        aria-describedby={undefined}
        // Log time opens over this one: a click there is not a click outside.
        onInteractOutside={(event) => {
          if (event.target instanceof Element && event.target.closest('[role="dialog"]')) event.preventDefault();
        }}
        headerActions={item ? (
          <Button variant="subtle" size="icon-sm" aria-label="Copy key" title="Copy key" onClick={copy}><CopyGlyph /></Button>
        ) : undefined}
      >
        {body}
      </ModalContent>
    </Modal>
  );
}

function Section({ title, aside, children }: { title: string; aside?: string | undefined; children: ReactNode }): JSX.Element {
  return (
    <section className="flex flex-col gap-2.5" aria-label={title}>
      <div className="flex items-center gap-2.5">
        <Text as="h3" size="sm" className="m-0 font-emphasis text-[var(--fg-strong)]">{title}</Text>
        {aside ? <span className="ml-auto text-xs text-[var(--fg-muted)]">{aside}</span> : null}
      </div>
      {children}
    </section>
  );
}

const Rows = ({ children }: { children: ReactNode }) => (
  <div className="flex flex-col overflow-hidden rounded-[var(--radius-lg)] border border-[var(--border)]">{children}</div>
);
const Row = ({ children, wrap = false }: { children: ReactNode; wrap?: boolean }) => (
  <div className={`flex min-h-10 items-center gap-2.5 border-b border-[var(--border-subtle)] px-3 text-sm last:border-b-0${wrap ? ' flex-wrap py-2' : ''}`}>{children}</div>
);
